import {
  apiErrorSchema,
  conversationSchema,
  modelRuntimeSchema,
  markerSchema,
  proposalSchema,
  sessionSchema,
  termDetailSchema,
  termListResponseSchema,
  turnSchema,
  type Conversation,
  type ModelRuntime,
  type Marker,
  type RevisionProposal,
  type Session,
  type TermDetail,
  type TermListResponse,
  type TermSort,
  type Turn,
} from "@termclear/contracts";
import type { ZodType } from "zod";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

class ApiClient {
  private csrfToken: string | null = null;

  async restoreSession(): Promise<Session | null> {
    try {
      const session = await this.request("/api/v1/session", { method: "GET" }, sessionSchema);
      this.csrfToken = session.csrfToken;
      return session;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  }

  async login(password: string): Promise<Session> {
    const session = await this.request(
      "/api/v1/session/login",
      { method: "POST", body: JSON.stringify({ password }) },
      sessionSchema,
    );
    this.csrfToken = session.csrfToken;
    return session;
  }

  async logout(): Promise<void> {
    await this.mutation("/api/v1/session", { method: "DELETE" }, null, crypto.randomUUID());
    this.csrfToken = null;
  }

  async listTerms(input: {
    search: string;
    collection: "technical" | "english" | "all";
    category: string;
    state: string;
    sort: TermSort;
    cursor?: string;
    randomSeed: number;
  }): Promise<TermListResponse> {
    const query = new URLSearchParams({
      search: input.search,
      collection: input.collection,
      category: input.category,
      state: input.state,
      sort: input.sort,
      randomSeed: String(input.randomSeed),
      limit: "20",
    });
    if (input.cursor) query.set("cursor", input.cursor);
    return this.request(`/api/v1/terms?${query}`, { method: "GET" }, termListResponseSchema);
  }

  getTerm(termId: string): Promise<TermDetail> {
    return this.request(`/api/v1/terms/${termId}`, { method: "GET" }, termDetailSchema);
  }

  async setRating(termId: string, needUnderstanding: number, idempotencyKey: string): Promise<void> {
    await this.mutation(
      `/api/v1/terms/${termId}/learning-state`,
      { method: "PATCH", body: JSON.stringify({ needUnderstanding }) },
      null,
      idempotencyKey,
    );
  }

  createMarker(termId: string, input: {
    blockId: string;
    baselineRevisionId: string;
    note: string;
  }, idempotencyKey: string): Promise<Marker> {
    return this.mutation(
      `/api/v1/terms/${termId}/markers`,
      { method: "POST", body: JSON.stringify(input) },
      markerSchema,
      idempotencyKey,
    );
  }

  createConversation(termId: string, input: {
    entryBlockId: string;
    baselineRevisionId: string;
  }, idempotencyKey: string): Promise<Conversation> {
    return this.mutation(
      `/api/v1/terms/${termId}/conversations`,
      { method: "POST", body: JSON.stringify(input) },
      conversationSchema,
      idempotencyKey,
    );
  }

  getConversation(conversationId: string): Promise<Conversation> {
    return this.request(
      `/api/v1/conversations/${conversationId}`,
      { method: "GET" },
      conversationSchema,
    );
  }

  getModelRuntime(): Promise<ModelRuntime> {
    return this.request(
      "/api/v1/model-runtime",
      { method: "GET" },
      modelRuntimeSchema,
    );
  }

  createTurn(conversationId: string, content: string, idempotencyKey: string): Promise<Turn> {
    return this.mutation(
      `/api/v1/conversations/${conversationId}/turns`,
      { method: "POST", body: JSON.stringify({ content }) },
      turnSchema,
      idempotencyKey,
    );
  }

  createProposal(conversationId: string, idempotencyKey: string): Promise<RevisionProposal> {
    return this.mutation(
      `/api/v1/conversations/${conversationId}/proposals`,
      { method: "POST", body: "{}" },
      proposalSchema,
      idempotencyKey,
    );
  }

  applyProposal(proposal: RevisionProposal, idempotencyKey: string): Promise<void> {
    return this.mutation(
      `/api/v1/proposals/${proposal.id}/apply`,
      {
        method: "POST",
        headers: { "If-Match": `"${proposal.baselineRevisionId}"` },
        body: JSON.stringify({ baselineRevisionId: proposal.baselineRevisionId }),
      },
      null,
      idempotencyKey,
    );
  }

  private mutation<T>(
    path: string,
    init: RequestInit,
    schema: ZodType<T> | null,
    idempotencyKey: string,
  ): Promise<T> {
    if (!this.csrfToken) throw new ApiError(401, "AUTH_REQUIRED", "登录状态已经失效");
    const headers = new Headers(init.headers);
    headers.set("X-CSRF-Token", this.csrfToken);
    headers.set("Idempotency-Key", idempotencyKey);
    return this.request(path, { ...init, headers }, schema);
  }

  private async request<T>(path: string, init: RequestInit, schema: ZodType<T> | null): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body) headers.set("Content-Type", "application/json");
    const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
    if (!response.ok) {
      const payload = apiErrorSchema.safeParse(await response.json().catch(() => null));
      if (payload.success) {
        throw new ApiError(
          response.status,
          payload.data.error.code,
          payload.data.error.message,
          payload.data.error.details,
        );
      }
      throw new ApiError(response.status, "UNKNOWN", "服务器返回了无法识别的错误");
    }
    if (response.status === 204 || schema === null) return undefined as T;
    return schema.parse(await response.json());
  }
}

export const api = new ApiClient();
